<template>
  <VueFusionCharts
    v-if="symbols.length"
    id="donut-chart"
    :key="colorMode"
    ref="chart"
    type="doughnut2d"
    data-format="json"
    height="100%"
    width="100%"
    :data-source="dataSource"
    @render-complete="handlePlotColors"
    @node-updated="handlePlotColors"
    @dataplot-click="handleClickOnNode"
    @dataplot-roll-over="handleHoverOnNode"
    @dataplot-roll-out="handleHoverOut"
  />

  <MarketMapSymbolDetail
    v-if="selectedIsin"
    :isin="selectedIsin"
    :style="hoverStyle"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import VueFusionChartsComponent from 'vue-fusioncharts/component';
import FusionCharts from 'fusioncharts';
import Charts from 'fusioncharts/fusioncharts.charts';
import { getSymbolIsin, getSymbolName, useSortSymbols } from '@core/symbol';
import { ORDER_TYPES } from '@core/order';
import { VirtualPortfolioItem } from '@libs/api-reference';
import { PortfolioItemWithPrices } from '@core/portfolio';

import { MarketMapSymbolDetail } from '@/modules/symbol';
import { useOrderDialogs } from '@/modules/order';
import { useColorMode } from '@vueuse/core';

type PortfolioDonutChartMapProps = {
  symbols: VirtualPortfolioItem[] | PortfolioItemWithPrices[];
};

const props = defineProps<PortfolioDonutChartMapProps>();

const BOTTOM_OFFSET = 160;
const OFFSET = 4;
const DISTANCE_FROM_CURSOR = 150;
const HOVERED_COMPONENT_HEIGHT = 680;
const HOVERED_COMPONENT_WIDTH = 400;
const SYMBOLS_TO_SHOW = 15;
const DEFAULT_HOVER_STYLE = {
  top: 'auto',
  bottom: 'auto',
  right: 'auto',
  left: 'auto',
  position: 'fixed',
  zIndex: 9999,
};

const chart = ref();
const dataSource = ref();
const selectedIsin = ref<string | null>();
const hoverStyle = ref<{
  top: string;
  bottom: string;
  right: string;
  left: string;
  position: string;
  zIndex: number;
}>(DEFAULT_HOVER_STYLE);

const VueFusionCharts = VueFusionChartsComponent(FusionCharts, Charts);

const { t } = useI18n();

const colorMode = useColorMode();

const { symbols: sortedSymbols } = useSortSymbols({
  defaultSymbols: computed(() => props.symbols),
  defaultSortValue: 'portfolioValue',
});

const { openNewOrderFormDialog } = useOrderDialogs();

const handlePlotColors = () => {
  document
    .querySelectorAll('#donut-chart g[class$="-plots"] path[stroke-opacity]')
    .forEach((element) => {
      element.setAttribute(
        'fill',
        `rgba(6, 97, 55, ${element.getAttribute('stroke-opacity')})`
      );
    });
};

const calcChartData = () => {
  const sortedSymbolsValue = sortedSymbols.value;

  const chartData: {
    label: string | null;
    value: number;
    color: string;
    alpha: number;
    id: string | null;
  }[] = [];

  const totalValues = sortedSymbolsValue.reduce(
    (partialSum, symbol) => partialSum + (symbol.value ?? 0),
    0
  );

  const numberOfTopSymbolsBasedOnValue =
    sortedSymbolsValue.length > SYMBOLS_TO_SHOW
      ? SYMBOLS_TO_SHOW
      : sortedSymbolsValue.length;

  const topSymbolsBasedOnValue = sortedSymbolsValue.slice(
    0,
    numberOfTopSymbolsBasedOnValue
  );

  if (sortedSymbolsValue.length > SYMBOLS_TO_SHOW) {
    const otherSymbols = sortedSymbolsValue.slice(
      numberOfTopSymbolsBasedOnValue
    );

    const otherSymbolsTotalValues = otherSymbols.reduce(
      (partialSum, symbol) => partialSum + (symbol.value ?? 0),
      0
    );

    chartData.push({
      label: `${sortedSymbolsValue.length - SYMBOLS_TO_SHOW}+ ${t(
        'common.symbol'
      )}`,
      value: (otherSymbolsTotalValues / totalValues) * 100,
      color: '#066137',
      alpha: 100 / (SYMBOLS_TO_SHOW + 1),
      id: 'others',
    });
  }

  const numberOfChartSlices =
    sortedSymbolsValue.length > SYMBOLS_TO_SHOW
      ? SYMBOLS_TO_SHOW
      : sortedSymbolsValue.length;

  topSymbolsBasedOnValue.reverse().forEach((symbol, index) => {
    chartData.push({
      label: getSymbolName(symbol),
      value: ((symbol.value ?? 0) / totalValues) * 100,
      color: '#066137',
      alpha: (index + 2) * (100 / (numberOfChartSlices + 1)),
      id: getSymbolIsin(symbol),
    });
  });

  return {
    chart: {
      numberPrefix: '%',
      decimals: '1',
      theme: 'fusion',
      bgAlpha: 0,
      labelFontColor: colorMode.value === 'dark' ? '#d8d8d8' : '#000',
      showBorder: 0,
      pieRadius: '180',
      doughnutRadius: '120',
      startingAngle: 90,
      showToolTip: 0,
      labelFont: 'iransansxv',
      labelFontSize: 13,
      enableSlicing: 0,
      animation: false,
      showShadow: 0,
      showPlotBorder: 0,
      plotFillAlpha: 0,
      smartLabel: 1,
    },
    data: chartData,
  };
};

const handleClickOnNode = ({ data }) => {
  const instrumentName = data.categoryLabel;
  const isin = data.id;

  if (isin && isin !== 'others') {
    openNewOrderFormDialog({
      type: ORDER_TYPES.buy,
      symbol: { isin, instrumentName },
    });
  }
};

const handleHoverOnNode = ({ data }) => {
  const isin = data.id;

  if (isin && isin !== 'others') {
    selectedIsin.value = isin;

    if (document.body.clientHeight - data.pageY > HOVERED_COMPONENT_HEIGHT) {
      hoverStyle.value.top = `${data.pageY}px`;
      hoverStyle.value.bottom = 'auto';
    } else {
      hoverStyle.value.top = 'auto';
      hoverStyle.value.bottom = `${BOTTOM_OFFSET}px`;
    }

    if (data.pageX > document.body.clientWidth / 2) {
      if (
        data.pageX - Number(HOVERED_COMPONENT_WIDTH) - DISTANCE_FROM_CURSOR <
        0
      ) {
        hoverStyle.value.left = `${OFFSET}px`;
      } else {
        hoverStyle.value.left = `${
          data.pageX - Number(HOVERED_COMPONENT_WIDTH) - DISTANCE_FROM_CURSOR
        }px`;
      }
    } else {
      if (
        data.pageX + DISTANCE_FROM_CURSOR + Number(HOVERED_COMPONENT_WIDTH) >
        document.body.clientWidth
      ) {
        hoverStyle.value.left = `${
          document.body.clientWidth - HOVERED_COMPONENT_WIDTH - OFFSET
        }px`;
      } else {
        hoverStyle.value.left = `${data.pageX + DISTANCE_FROM_CURSOR}px`;
      }
    }
  }
};

const handleHoverOut = () => {
  selectedIsin.value = null;

  hoverStyle.value = DEFAULT_HOVER_STYLE;
};

watch(
  sortedSymbols,
  () => {
    dataSource.value = calcChartData();
  },
  {
    immediate: true,
    deep: true,
  }
);
</script>

<style>
#donut-chart [pointer-events='bounding-box'] {
  display: none;
}

#donut-chart [stroke='#767575'] {
  stroke: rgba(0, 0, 0, 0.3);
}

#donut-chart g[class$='-plots'] path[stroke-opacity]:first-child {
  cursor: default;
}

#donut-chart g[class$='-plots'] path[stroke-opacity] {
  cursor: pointer;
}

#donut-chart text {
  direction: rtl;
}

#donut-chart text[text-anchor='end'] {
  text-anchor: start !important;
}

#donut-chart text[text-anchor='start'] {
  text-anchor: end !important;
}

#donut-chart * {
  background-color: transparent !important;
}
</style>
